import { lazy, Suspense } from 'preact/compat';
import { useCallback, useState } from 'preact/hooks';

import AnimalGrid from '@/components/AnimalGrid';
import { useWindowMediaQuery } from '@/hooks/useWindowMediaQuery';
import { AnyCard } from '@/queries/cards/types';
import { HabitatAnimal } from '@/queries/habitats/types';

import HabitatMediaGrid from './MediaGrid';

const LazyCardsModal = lazy(() => import('@/components/CardsModal'));

interface HabitatEmbedPanelProps {
  habitatId: string;
  animals: HabitatAnimal[] | undefined;
}

const HabitatEmbedPanel = ({ habitatId, animals }: HabitatEmbedPanelProps) => {
  const isDesktopSize = useWindowMediaQuery('(min-width: 768px)');

  const [selectedAnimalCards, setSelectedAnimalCards] = useState<AnyCard[] | null>(null);
  const [cardIndex, setCardIndex] = useState(0);

  const onNextCard = useCallback(() => setCardIndex((prev) => prev + 1), []);
  const onPrevCard = useCallback(() => setCardIndex((prev) => Math.max(prev - 1, 0)), []);
  const onClose = useCallback(() => {
    setSelectedAnimalCards(null);
  }, []);
  const onSelectAnimal = useCallback((animalId: string, cards: AnyCard[]) => {
    setSelectedAnimalCards(cards);
    setCardIndex(0);
  }, []);

  return (
    <>
      <div className="customScrollBar grey !overflow-x-auto !overflow-y-hidden">
        <AnimalGrid
          habitatId={habitatId}
          animals={animals}
          className="flex w-max gap-x-4 px-5 pb-3 pt-4 md:pt-5"
          thumbnailClassName="size-[72px]"
          titleClassName="text-sm/none font-semibold"
          onSelectAnimal={onSelectAnimal}
        />
      </div>

      <div className="px-5 pt-3">
        <section>
          <h4 className="preflight preflight-h4 text-xs font-normal text-[#57534E]">Moments Ago</h4>
          <HabitatMediaGrid
            className="my-2 grid grid-cols-2 gap-2"
            mediaQueryParams={{ habitatId, pageSize: 2, mediaType: 'video', videoType: 'clip' }}
            pageSize={2}
          />
        </section>

        <section>
          <h4 className="preflight preflight-h4 text-xs font-normal text-[#57534E]">Top Moments this Week</h4>
          <HabitatMediaGrid
            className="my-2 grid grid-cols-2 gap-2"
            mediaQueryParams={{
              habitatId,
              pageSize: 6,
              mediaType: 'video',
              videoType: 'clip',
              highlight: (0b11).toString(),
            }}
            pageSize={6}
          />
        </section>
      </div>

      {selectedAnimalCards && (
        <Suspense fallback={null}>
          <LazyCardsModal
            relative={isDesktopSize}
            loading={false}
            cards={selectedAnimalCards}
            activeCardIndex={cardIndex}
            onNextCard={onNextCard}
            onPrevCard={onPrevCard}
            onClose={onClose}
            habitatId={habitatId}
          />
        </Suspense>
      )}
    </>
  );
};

export default HabitatEmbedPanel;
